import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import api from '../../utils/webcall';
import { getErrorMessage } from '../../utils/utils';

/**
 * How the business runs its payroll. Owners only.
 *
 * Three numbers, and each one changes what everybody can do, so each says what
 * it means in plain terms rather than assuming the label is enough.
 */
function PayrollSettingsDialog(props) {
    const { open, onClose, onSaved, showErrorMessage } = props;
    const isMobile = useMediaQuery('(max-width:600px)');

    const [form, setForm] = useState({
        advance_hold_days: '12', default_annual_leave_days: '12',
        payroll_start: '',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get('/staff/settings/').then((res) => {
            const data = res.data.settings || {};
            setForm({
                advance_hold_days: String(data.advance_hold_days ?? 12),
                default_annual_leave_days: String(data.default_annual_leave_days ?? 12),
                payroll_start: data.payroll_start || '',
            });
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => setLoading(false));
    }, [open, showErrorMessage]);

    const set = (field) => (event) => {
        setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const submit = () => {
        setSaving(true);
        api.patch('/staff/settings/', form)
            .then(() => onSaved())
            .catch((error) => showErrorMessage(getErrorMessage(error)))
            .finally(() => setSaving(false));
    };

    const hold = Number(form.advance_hold_days) || 0;
    const annual = Number(form.default_annual_leave_days) || 0;

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose}
            fullWidth maxWidth="sm" fullScreen={isMobile}>
            <DialogTitle>Payroll settings</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Typography variant="body2" sx={{ py: 2 }}>Loading...</Typography>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Days held back" fullWidth
                                inputMode="numeric" value={form.advance_hold_days}
                                onFocus={(e) => e.target.select()}
                                onChange={set('advance_hold_days')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Annual leave for new staff" fullWidth
                                inputMode="decimal"
                                value={form.default_annual_leave_days}
                                onFocus={(e) => e.target.select()}
                                onChange={set('default_annual_leave_days')}
                                helperText="Days a year" />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Alert severity="info">
                                Wages are held for {hold} day{hold === 1 ? '' : 's'} after
                                a month ends, so June&rsquo;s salary is payable
                                on {hold === 12 ? '12 July' : `${hold} days after 30 June`}.
                                Nobody can draw an advance against days still
                                inside the hold. {annual > 0 && (
                                    <>Leave builds up
                                    at {(annual / 12).toFixed(2)} days a month and
                                    stops at {annual}; anything above that is
                                    forfeited.</>
                                )}
                            </Alert>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Records start" type="date" fullWidth
                                value={form.payroll_start} onChange={set('payroll_start')}
                                InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            {/* This one is easy to set carelessly and expensive to
                                get wrong, so it explains itself rather than
                                relying on the label. */}
                            <Typography variant="caption" color="text.secondary">
                                Nothing before the records start date counts. Move it
                                back and everyone accrues leave from earlier, and can
                                draw advances against wages this system never paid
                                them. Use each person&rsquo;s &ldquo;leave carried
                                in&rdquo; to record what they had already earned
                                instead.
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={saving || loading}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default PayrollSettingsDialog;
